/**
 * SectionCard v2 — title-first, customer-friendly card for the IT Act mapper.
 *
 * Reading order matches a customer's mental model:
 *   1. Tag pills at top-right ("Limit changed", "Renumbered" — what's the
 *      change?).
 *   2. Big display-font title — what this section is *about*.
 *   3. Plain-English summary — what it means for me.
 *   4. Old → new ref pair as a small footer line — section number trivia.
 *   5. Single "Show details" toggle — opens an inline panel with three tabs:
 *        For you · Read the law · See also  (renamed from variants/act/related)
 *
 * No floating CBDT ribbon, no copy/export buttons in the card corner — those
 * actions live inside the expanded view to keep the rest state quiet.
 */
import * as React from 'react';
import {
  ChevronDown, ChevronUp, AlertCircle, CheckCircle2,
  Sparkles, BookOpen, Link2, Copy, Download, NotebookPen,
} from 'lucide-react';
import type {
  Section, SectionVariant, SectionRelationship, RelationshipType, ChangeType,
} from '../types';

/**
 * Pulls every non-empty caveat from a section's variants.
 *
 * `variant.caveat` is where the dataset hides the *most actionable* expert
 * advice — things like "STT must have been paid" or "Cash payment for
 * premium not allowed". 22 of the 115 sections have at least one. We lift
 * them to the card surface so users don't have to drill three levels deep.
 */
function collectCaveats(section: Section): string[] {
  if (!section.variants?.length) return [];
  return section.variants
    .map((v) => v.caveat?.trim())
    .filter((c): c is string => !!c);
}

// ── Palette ───────────────────────────────────────────────────────────────────
const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';
const MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';

// ── Pill colors ───────────────────────────────────────────────────────────────

const CHANGE_PILL: Record<ChangeType, { label: string; bg: string; color: string; border: string }> = {
  renumbered: { label: 'Renumbered', bg: 'hsl(217 91% 60% / 0.10)', color: 'hsl(217 91% 38%)', border: 'hsl(217 91% 60% / 0.25)' },
  amended:    { label: 'Amended',    bg: `hsl(33 100% 50% / 0.12)`, color: 'hsl(33 100% 35%)', border: `hsl(33 100% 50% / 0.30)` },
  merged:     { label: 'Merged',     bg: 'hsl(265 84% 65% / 0.10)', color: 'hsl(265 70% 45%)', border: 'hsl(265 84% 65% / 0.25)' },
  relocated:  { label: 'Relocated',  bg: 'hsl(174 62% 47% / 0.12)', color: 'hsl(174 60% 28%)', border: 'hsl(174 62% 47% / 0.25)' },
  deleted:    { label: 'Removed',    bg: 'hsl(0 72% 60% / 0.10)',   color: 'hsl(0 72% 40%)',   border: 'hsl(0 72% 60% / 0.25)'   },
};

const REL_STYLES: Record<RelationshipType, { label: string; bg: string; color: string; border: string }> = {
  READ_WITH:            { label: 'Read with',     bg: 'hsl(217 91% 60% / 0.08)', color: 'hsl(217 91% 38%)', border: 'hsl(217 91% 60% / 0.25)' },
  GOVERNED_BY:          { label: 'Governed by',   bg: 'hsl(33 100% 50% / 0.10)', color: 'hsl(33 100% 35%)', border: 'hsl(33 100% 50% / 0.30)' },
  FORM_REQUIRED:        { label: 'Form required', bg: 'hsl(var(--muted))',       color: 'hsl(var(--muted-foreground))', border: 'hsl(var(--border))' },
  EXEMPTION_AVAILABLE:  { label: 'Exemption',     bg: 'hsl(142 60% 45% / 0.10)', color: 'hsl(142 60% 28%)', border: 'hsl(142 60% 45% / 0.30)' },
  AGGREGATE_CAP_SHARED: { label: 'Shared cap',    bg: 'hsl(265 84% 65% / 0.08)', color: 'hsl(265 70% 45%)', border: 'hsl(265 84% 65% / 0.25)' },
};

const REGIME_PILL: Record<string, { label: string; bg: string; color: string }> = {
  new:  { label: 'New regime',   bg: 'hsl(217 91% 60% / 0.10)', color: 'hsl(217 91% 38%)' },
  old:  { label: 'Old regime',   bg: 'hsl(33 100% 50% / 0.10)', color: 'hsl(33 100% 35%)' },
  both: { label: 'Both regimes', bg: 'hsl(142 60% 45% / 0.10)', color: 'hsl(142 60% 28%)' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

type DetailTab = 'meaning' | 'law' | 'related';
type ActVersion = '2025' | '1961';

function exportSectionText(section: Section): void {
  const lines: string[] = [
    'FinminOS — Income Tax Act reference',
    '',
    `${section.old_ref} → ${section.new_ref}`,
    `${section.old_title}${section.new_title !== section.old_title ? ` → ${section.new_title}` : ''}`,
    '',
    'Summary:',
    section.plain_english_summary,
  ];
  if (section.limit_changed_note) lines.push('', `Limit change: ${section.limit_changed_note}`);
  if (section.variants?.length) {
    lines.push('', 'How it applies:');
    section.variants.forEach((v) => {
      lines.push(`  • ${v.label}: ${v.tax_treatment}`);
      if (v.caveat) lines.push(`    ⚠ ${v.caveat}`);
    });
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${section.old_ref.replace(/\s/g, '_')}_to_${section.new_ref.replace(/[\s/]/g, '_')}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Card ──────────────────────────────────────────────────────────────────────

export function SectionCard({ section, index = 0 }: { section: Section; index?: number }) {
  const [expanded, setExpanded] = React.useState(false);
  const [tab, setTab]           = React.useState<DetailTab>('meaning');
  const [actVersion, setActVer] = React.useState<ActVersion>('2025');
  const [copied, setCopied]     = React.useState(false);

  const isDeleted    = section.change_type === 'deleted';
  const hasVariants  = !!section.variants?.length;
  const hasRelations = !!section.relationships?.length;
  const hasActText   = !!section.source_text_available;
  const stagger      = `stagger-${Math.min((index % 8) + 1, 8)}`;

  // CA caveats — surfaced on the card itself instead of hiding inside variants.
  const caveats      = React.useMemo(() => collectCaveats(section), [section]);

  const changePill   = CHANGE_PILL[section.change_type];
  // Decide which tab opens first based on what data the section actually has.
  React.useEffect(() => {
    if (!expanded) return;
    if (hasVariants)        setTab('meaning');
    else if (hasActText)    setTab('law');
    else if (hasRelations)  setTab('related');
  }, [expanded, hasVariants, hasActText, hasRelations]);

  const titleToShow =
    section.new_title !== section.old_title && !isDeleted
      ? section.new_title
      : section.old_title;

  const handleCopy = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(`${section.old_ref} → ${section.new_ref}`)
      .catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [section.old_ref, section.new_ref]);

  return (
    <article
      className={`rounded-2xl border border-border bg-background transition-all duration-200 hover:shadow-md hover:border-foreground/15 animate-slide-up-fade ${stagger}`}
    >
      {/* Clickable header — toggling expand. Whole header is the hit area. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full text-left p-4 sm:p-5 group"
      >
        {/* Top row — change-type tags */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {section.change_type !== 'renumbered' && changePill && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                style={{ backgroundColor: changePill.bg, color: changePill.color, border: `1px solid ${changePill.border}` }}
              >
                {changePill.label}
              </span>
            )}
            {section.limit_changed && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                style={{ backgroundColor: `${AMBER}15`, color: 'hsl(33 100% 35%)', border: `1px solid ${AMBER}40` }}
              >
                <AlertCircle className="h-2.5 w-2.5" aria-hidden />
                Limit changed
              </span>
            )}
          </div>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </div>

        {/* Title */}
        <h3 className={`font-display font-semibold text-lg sm:text-xl text-foreground leading-tight tracking-tight ${isDeleted ? 'opacity-70' : ''}`}>
          {titleToShow}
        </h3>

        {/* Plain-English summary */}
        <p className="text-sm text-muted-foreground leading-relaxed mt-1.5">
          {section.plain_english_summary}
        </p>

        {/* CA's note — surfaces variant caveats at card level so the most
            actionable expert advice isn't buried 3 clicks deep. */}
        {caveats.length > 0 && (
          <CaNoteCallout
            caveats={caveats}
            onSeeMore={(e) => {
              e.stopPropagation();
              setExpanded(true);
              setTab('meaning');
            }}
          />
        )}

        {/* Footer metadata — old → new ref pair */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-2.5 border-t border-border text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="font-mono font-semibold" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {section.old_ref}
            </span>
            <span aria-hidden>→</span>
            <span className={`font-mono font-semibold ${isDeleted ? 'line-through opacity-60' : ''}`} style={{ color: FOREST }}>
              {section.new_ref}
            </span>
            <span className="text-[10px] opacity-70">(1961 → 2025)</span>
          </span>
          {section.cbdt_verified && (
            <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: 'hsl(142 60% 35%)' }}>
              <CheckCircle2 className="h-3 w-3" aria-hidden />
              CBDT verified
            </span>
          )}
          {section.category && (
            <span className="text-[10px] uppercase tracking-widest opacity-80">
              {section.category}
            </span>
          )}
        </div>
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 animate-slide-down-fade origin-top">
          {/* Limit-change strip — only inside expand, not on the card surface */}
          {section.limit_changed && section.limit_changed_note && (
            <div
              className="flex items-start gap-2 rounded-xl border px-3 py-2 mb-3"
              style={{ backgroundColor: `${AMBER}08`, borderColor: `${AMBER}40` }}
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: AMBER }} aria-hidden />
              <div className="flex-1 min-w-0 text-xs leading-relaxed">
                <span className="font-semibold text-foreground">Monetary limit changed.</span>{' '}
                <span className="text-muted-foreground">{section.limit_changed_note}</span>
                {section.old_limit != null && section.new_limit != null && (
                  <span className="ml-2 inline-flex items-center gap-1 font-mono">
                    <span className="text-muted-foreground line-through">
                      ₹{section.old_limit.toLocaleString('en-IN')}
                    </span>
                    <span aria-hidden>→</span>
                    <span className="font-semibold" style={{ color: FOREST }}>
                      ₹{section.new_limit.toLocaleString('en-IN')}
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Tab bar — inline tabs using simple buttons (lighter than the
              full AnimatedTabs since this is small-scope) */}
          <div className="flex gap-1 mb-3 border-b border-border -mx-1 px-1">
            <DetailTabBtn
              label="What this means"
              icon={Sparkles}
              active={tab === 'meaning'}
              disabled={!hasVariants}
              onClick={() => setTab('meaning')}
            />
            <DetailTabBtn
              label="Read the law"
              icon={BookOpen}
              active={tab === 'law'}
              disabled={!hasActText}
              onClick={() => setTab('law')}
            />
            <DetailTabBtn
              label={`See also${hasRelations ? ` (${section.relationships!.length})` : ''}`}
              icon={Link2}
              active={tab === 'related'}
              disabled={!hasRelations}
              onClick={() => setTab('related')}
            />
          </div>

          {/* Tab content */}
          <div className="min-h-[60px]">
            {tab === 'meaning' && (
              hasVariants
                ? <VariantList variants={section.variants!} />
                : <EmptyTab message="No special variants — the section applies as written." />
            )}
            {tab === 'law' && (
              hasActText
                ? <ActTextPanel section={section} version={actVersion} setVersion={setActVer} />
                : <EmptyTab message="The official act text isn't extracted for this section yet." />
            )}
            {tab === 'related' && (
              hasRelations
                ? <RelationshipList relationships={section.relationships!} />
                : <EmptyTab message="No related sections recorded." />
            )}
          </div>

          {/* Footer actions — copy + export, hidden until expand */}
          <div className="flex items-center justify-end gap-1.5 mt-4 pt-3 border-t border-border">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
            >
              {copied
                ? <><CheckCircle2 className="h-3 w-3" /> Copied</>
                : <><Copy className="h-3 w-3" /> Copy reference</>
              }
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); exportSectionText(section); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
            >
              <Download className="h-3 w-3" /> Export
            </button>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex items-center gap-1 ml-auto px-2.5 py-1 rounded-md text-xs font-semibold transition-colors hover:opacity-70"
              style={{ color: FOREST }}
            >
              Hide details
              <ChevronUp className="h-3 w-3" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

// ── CA note callout — visible on the card surface ────────────────────────────

function CaNoteCallout({
  caveats, onSeeMore,
}: {
  caveats: string[];
  onSeeMore: (e: React.MouseEvent) => void;
}) {
  // We show the first caveat verbatim (truncated to ~2 lines via line-clamp).
  // If there are more, a small "+N more" link nudges the user to expand.
  const first = caveats[0];
  const more  = caveats.length - 1;

  return (
    <div
      className="mt-3 rounded-xl px-3 py-2.5 flex items-start gap-2.5 border"
      style={{
        backgroundColor: MINT,
        borderColor:     'hsl(var(--border))',
      }}
    >
      <div
        className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: 'hsl(var(--palette-cream, 40 100% 98%) / 0.9)' }}
      >
        <NotebookPen className="h-3.5 w-3.5" style={{ color: FOREST }} aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-[10px] font-semibold uppercase tracking-widest mb-0.5"
          style={{ color: FOREST }}
        >
          CA's note
        </p>
        <p className="text-xs text-foreground leading-relaxed line-clamp-2">
          {first}
        </p>
        {more > 0 && (
          <button
            type="button"
            onClick={onSeeMore}
            className="mt-1 text-[11px] font-semibold underline underline-offset-2 hover:opacity-70 transition-opacity"
            style={{ color: FOREST }}
          >
            +{more} more {more === 1 ? 'note' : 'notes'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Inline tab button ─────────────────────────────────────────────────────────

function DetailTabBtn({
  label, icon: Icon, active, disabled, onClick,
}: {
  label: string;
  icon: React.ElementType;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      style={
        active
          ? { borderColor: FOREST, color: FOREST }
          : { borderColor: 'transparent', color: 'hsl(var(--muted-foreground))' }
      }
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </button>
  );
}

function EmptyTab({ message }: { message: string }) {
  return (
    <p className="text-xs text-muted-foreground italic py-4 text-center">{message}</p>
  );
}

// ── Variant list (under "What this means") ──────────────────────────────────

function VariantList({ variants }: { variants: SectionVariant[] }) {
  return (
    <div className="space-y-2">
      {variants.map((v) => {
        const rp = v.conditions.regime ? REGIME_PILL[v.conditions.regime] : null;
        return (
          <div
            key={v.id}
            className="rounded-xl border border-border p-3"
            style={{ backgroundColor: 'hsl(var(--background))' }}
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <span className="text-sm font-semibold text-foreground leading-tight">
                {v.label}
              </span>
              {rp && (
                <span
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{ backgroundColor: rp.bg, color: rp.color }}
                >
                  {rp.label}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{v.tax_treatment}</p>
            {v.caveat && (
              <div
                className="mt-2 flex items-start gap-1.5 rounded-md px-2 py-1.5"
                style={{ backgroundColor: `${AMBER}12`, color: 'hsl(33 100% 35%)' }}
              >
                <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" aria-hidden />
                <span className="text-[11px] leading-snug">{v.caveat}</span>
              </div>
            )}
            {v.conditions.taxpayer_type && v.conditions.taxpayer_type.length > 0 && (
              <p className="text-[10px] text-muted-foreground mt-1.5">
                Applies to: {v.conditions.taxpayer_type.join(', ')}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Relationship list (under "See also") ─────────────────────────────────────

function RelationshipList({ relationships }: { relationships: SectionRelationship[] }) {
  return (
    <div className="divide-y divide-border">
      {relationships.map((rel, i) => {
        const s = REL_STYLES[rel.type] ?? REL_STYLES.READ_WITH;
        return (
          <div key={i} className="flex items-start gap-3 py-2.5">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap shrink-0 mt-0.5"
              style={{ backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}` }}
            >
              {s.label}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {rel.target_ref}
                <span className="ml-2 font-normal text-muted-foreground">— {rel.target_title}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{rel.note}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Act text panel (under "Read the law") ────────────────────────────────────

function ActTextPanel({
  section, version, setVersion,
}: {
  section: Section;
  version: ActVersion;
  setVersion: (v: ActVersion) => void;
}) {
  const [showSubs, setShowSubs] = React.useState(false);
  const st = section.source_text;

  return (
    <div>
      {/* Version switcher */}
      <div className="flex gap-1.5 mb-3">
        {(['2025', '1961'] as ActVersion[]).map((v) => {
          const active = version === v;
          return (
            <button
              key={v}
              type="button"
              onClick={(e) => { e.stopPropagation(); setVersion(v); }}
              className="px-3 py-1 rounded-lg text-xs font-semibold transition-all border"
              style={
                active
                  ? { backgroundColor: FOREST, color: 'hsl(var(--palette-cream))', borderColor: FOREST }
                  : { backgroundColor: 'transparent', color: 'hsl(var(--muted-foreground))', borderColor: 'hsl(var(--border))' }
              }
            >
              IT Act {v}
            </button>
          );
        })}
      </div>

      {version === '1961' || !st ? (
        <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            {version === '1961'
              ? <>The 1961 act text for <span className="font-mono text-foreground">{section.old_ref}</span> is in the original Act. This mapper shows the 2025 text.</>
              : 'Act text not yet extracted for this section.'
            }
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-border px-4 py-3" style={{ backgroundColor: MINT }}>
            <p className="text-sm font-semibold text-foreground leading-relaxed mb-2">
              {st.act_2025.section_header}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {st.act_2025.main_provision}
            </p>
            {st.act_2025.sub_sections && Object.keys(st.act_2025.sub_sections).length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowSubs((v) => !v); }}
                  className="inline-flex items-center gap-1 text-xs font-semibold transition-colors"
                  style={{ color: FOREST }}
                >
                  {showSubs ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  {showSubs ? 'Hide' : 'Show'} sub-sections
                  ({Object.keys(st.act_2025.sub_sections).length})
                </button>
                {showSubs && (
                  <div className="mt-2 space-y-2">
                    {Object.entries(st.act_2025.sub_sections).map(([k, v]) => (
                      <div key={k} className="text-xs leading-relaxed">
                        <span className="font-mono font-semibold text-foreground mr-2">{k}</span>
                        <span className="text-muted-foreground">{v}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="mt-2 text-[10px] font-mono text-muted-foreground inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: 'hsl(142 60% 45%)' }} />
            Extracted from official PDF · Page {st.pdf_page} · Verified {st.extraction_date}
          </p>
        </>
      )}
    </div>
  );
}
